// src/pages/ClanManagePage/ClanManagePage.jsx

import React, { useState, useEffect, useCallback } from "react";
import {
  useParams,
  useOutletContext,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import "./ClanManagePage.css";
import Modal from "../../components/Modal/Modal";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { SquarePen, MoreVertical } from "lucide-react";

import ClanMembersTab from "../../components/ClanManageTabs/ClanMembersTab/ClanMembersTab";
import ClanEventsTab from "../../components/ClanManageTabs/ClanEventsTab/ClanEventsTab";
import ClanTasksTab from "../../components/ClanManageTabs/ClanTasksTab/ClanTasksTab";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
const MESSAGE_DISPLAY_DURATION = 4000;
const MESSAGE_FADE_DURATION = 1000;

const ClanSettingsDropdown = ({
  onDisbandClick,
  isActionInProgress,
  canOwnerDisband,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        isOpen &&
        event.target.closest(".settings-dropdown-container") === null
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  if (!canOwnerDisband) {
    return null;
  }

  return (
    <div className="settings-dropdown-container">
      <button
        className="btn-icon btn-secondary-icon"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isActionInProgress}
        title="Clan Settings and Actions"
      >
        <MoreVertical size={20} />
      </button>

      {isOpen && (
        <div className="settings-dropdown-menu">
          <div className="dropdown-item-header">Clan Management Actions</div>

          <button
            className="dropdown-item-button dropdown-item-danger"
            onClick={() => {
              setIsOpen(false);
              onDisbandClick();
            }}
            disabled={isActionInProgress}
          >
            Disband Clan
          </button>
        </div>
      )}
    </div>
  );
};

function ClanManagePage() {
  const { clanId } = useParams();
  const navigate = useNavigate();
  const { userData, refreshUserData } = useOutletContext();

  const [clanName, setClanName] = useState("Loading Clan...");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [actionMessage, setActionMessage] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isMessageFading, setIsMessageFading] = useState(false);
  const [messageKey, setMessageKey] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "members";

  const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
  const [newClanNameInput, setNewClanNameInput] = useState("");
  const [isDisbandModalOpen, setIsDisbandModalOpen] = useState(false);

  const currentUserId = userData?.id;

  const handleTabChange = (tabName) => {
    setSearchParams({ tab: tabName });
  };

  const getErrorMessage = useCallback((err, defaultMessage) => {
    if (err.response && err.response.data && err.response.data.error) {
      return err.response.data.error;
    }
    return defaultMessage;
  }, []);

  const setActionMessageWithKey = useCallback((message) => {
    setActionMessage(message);
    setMessageKey((prev) => prev + 1);
    setIsMessageFading(false);
  }, []);

  useEffect(() => {
    let fadeTimer;
    let clearTimer;

    if (actionMessage) {
      fadeTimer = setTimeout(() => {
        setIsMessageFading(true);
      }, MESSAGE_DISPLAY_DURATION);

      clearTimer = setTimeout(() => {
        setActionMessage(null);
        setIsMessageFading(false);
      }, MESSAGE_DISPLAY_DURATION + MESSAGE_FADE_DURATION);
    }

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(clearTimer);
    };
  }, [actionMessage, messageKey]);

  const sortMembers = (membersArray) => {
    const roleSortOrder = { Owner: 1, Admin: 2, Member: 3 };
    return [...membersArray].sort((a, b) => {
      const roleA = roleSortOrder[a.role];
      const roleB = roleSortOrder[b.role];
      if (roleA !== roleB) {
        return roleA - roleB;
      }
      return a.username.localeCompare(b.username);
    });
  };

  const fetchClanData = useCallback(
    async (signal, resolveTimer) => {
      const endpoint = `${API_BASE_URL}/clan/${clanId}/members`;
      setError(null);

      try {
        const response = await axios.get(endpoint, {
          withCredentials: true,
          signal: signal,
        });
        const memberList = response.data.members;

        if (memberList.length > 0) {
          const name = memberList[0]?.clan_name;
          setClanName(name || `Clan ID ${clanId}`);
        } else {
          setClanName(`Clan ID ${clanId}`);
        }
        setMembers(sortMembers(memberList));
      } catch (err) {
        if (axios.isCancel(err)) {
          return;
        }
        console.error("Error fetching clan members:", err);
        if (err.response && err.response.status === 401) {
          localStorage.removeItem("userToken");
          navigate("/auth");
        } else if (err.response && err.response.status === 404) {
          setError(`Clan ID ${clanId} not found.`);
        } else if (err.response && err.response.status === 403) {
          setError(
            "Permission denied. You are not authorized to manage this clan.",
          );
        } else {
          setError("Failed to load clan data.");
        }
      } finally {
        if (resolveTimer) {
          resolveTimer();
        }
      }
    },
    [clanId, navigate],
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let timerId;

    if (!isRedirecting) {
      const minDelayPromise = new Promise((resolve) => {
        timerId = setTimeout(resolve, 200);
      });

      const fetchDataPromise = new Promise((resolve) => {
        fetchClanData(signal, resolve);
      });

      setLoading(true);
      Promise.all([fetchDataPromise, minDelayPromise]).finally(() => {
        setLoading(false);
      });
    }

    return () => {
      controller.abort();
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [fetchClanData, isRedirecting]);

  const handleEditNameClick = () => {
    setNewClanNameInput(clanName);
    setIsEditNameModalOpen(true);
  };

  const executeEditName = async () => {
    const trimmedNewName = newClanNameInput.trim();
    setIsEditNameModalOpen(false);
    setActionMessage(null);

    if (!trimmedNewName || trimmedNewName === clanName) {
      setActionMessageWithKey({
        type: "error",
        message: "Clan name cannot be empty or the same as the current name.",
      });
      return;
    }

    setIsProcessingAction(true);

    try {
      const response = await axios.put(
        `${API_BASE_URL}/clan/${clanId}/name`,
        { new_name: trimmedNewName },
        { withCredentials: true },
      );

      const updatedName = response.data.new_name;
      setClanName(updatedName);

      setMembers((currentMembers) =>
        currentMembers.map((m) => ({
          ...m,
          clan_name: updatedName,
        })),
      );

      if (refreshUserData) {
        await refreshUserData();
      }

      setActionMessageWithKey({
        type: "success",
        message: `Clan name successfully updated to: ${updatedName}`,
      });
    } catch (err) {
      console.error("Edit clan name failed:", err);
      const errorMessage = getErrorMessage(
        err,
        "Failed to update clan name. Please check permissions and input rules.",
      );
      setActionMessageWithKey({ type: "error", message: errorMessage });
    } finally {
      setIsProcessingAction(false);
    }
  };

  const executeDisband = async () => {
    setIsDisbandModalOpen(false);
    setActionMessageWithKey({
      type: "success",
      message: `${clanName} was successfully disbanded. Redirecting...`,
    });
    setIsProcessingAction(true);

    setTimeout(async () => {
      try {
        await axios.delete(`${API_BASE_URL}/clan/${clanId}`, {
          withCredentials: true,
        });

        if (refreshUserData) {
          await refreshUserData();
        }

        setIsRedirecting(true);
        navigate("/profile");
      } catch (err) {
        const errorMessage = getErrorMessage(
          err,
          "Failed to disband clan. Please retry.",
        );
        setActionMessageWithKey({ type: "error", message: errorMessage });
        setIsProcessingAction(false);
        setIsRedirecting(false);
      }
    }, 500);
  };

  const handleDisbandClick = () => {
    setIsDisbandModalOpen(true);
  };

  const currentUserMembership = members.find(
    (m) => Number(m.member_id) === Number(currentUserId),
  );

  const isCurrentUserManager =
    currentUserMembership &&
    (currentUserMembership.role === "Admin" ||
      currentUserMembership.role === "Owner");

  const isCurrentUserOwner =
    currentUserMembership && currentUserMembership.role === "Owner";

  const totalManagers = members.filter(
    (m) => m.role === "Admin" || m.role === "Owner",
  ).length;

  const canOwnerDisband = isCurrentUserOwner;

  const isActionInProgress = isProcessingAction && !isRedirecting;

  const updateMembersList = (newMembersList) => {
    setMembers(sortMembers(newMembersList));
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case "members":
        return (
          <ClanMembersTab
            clanId={clanId}
            members={members}
            updateMembersList={updateMembersList}
            currentUserId={currentUserId}
            isCurrentUserManager={isCurrentUserManager}
            isCurrentUserOwner={isCurrentUserOwner}
            isActionInProgress={isActionInProgress}
            totalManagers={totalManagers}
            clanName={clanName}
            refreshUserData={refreshUserData}
            navigate={navigate}
            getErrorMessage={getErrorMessage}
            fetchClanData={fetchClanData}
            setActionMessage={setActionMessageWithKey}
            setIsProcessingAction={setIsProcessingAction}
            setIsRedirecting={setIsRedirecting}
          />
        );
      case "events":
        return (
          <ClanEventsTab
            clanId={clanId}
            clanName={clanName}
            isActionInProgress={isActionInProgress}
          />
        );
      case "tasks":
        return (
          <ClanTasksTab
            clanId={clanId}
            clanName={clanName}
            isActionInProgress={isActionInProgress}
            isCurrentUserManager={isCurrentUserManager}
            setActionMessage={setActionMessageWithKey}
            setIsProcessingAction={setIsProcessingAction}
          />
        );
      default:
        return null;
    }
  };

  if (isRedirecting) {
    return (
      <div className="clan-manage-container redirecting-state">
        <div className="spinner"></div>
        <p className="loading-message">
          {actionMessage?.message || "Finalizing action and redirecting..."}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="clan-manage-container loading-state">
        <div className="spinner"></div>
        <p>Loading clan management data...</p>
      </div>
    );
  }

  if (error) {
    return <div className="clan-manage-container error">Error: {error}</div>;
  }

  return (
    <>
      <div className="clan-manage-container">
        <Breadcrumb
          previousPageName={"Profile"}
          currentPageName={`Manage Clan: ${clanName}`}
        />

        <div className="title-and-edit">
          <h1>
            <span className="title-prefix">Manage Clan:</span>
            <span className="clan-name-actual">{clanName}</span>
          </h1>
          <div className="title-action-group">
            {isCurrentUserOwner && (
              <button
                className="btn-icon btn-secondary-icon"
                onClick={handleEditNameClick}
                title="Edit Clan Name"
                disabled={isActionInProgress}
              >
                <SquarePen size={20} />
              </button>
            )}

            <ClanSettingsDropdown
              onDisbandClick={handleDisbandClick}
              isActionInProgress={isActionInProgress}
              canOwnerDisband={canOwnerDisband}
            />
          </div>
        </div>

        <div className="dashboard-header-controls">
          <p className="total-members-count">
            Total Managers: {totalManagers} / Total Members: {members.length}
          </p>

          <div className="action-buttons-group"></div>
        </div>

        <div className="clan-tabs-nav">
          <button
            className={`tab-button ${activeTab === "members" ? "active" : ""}`}
            onClick={() => handleTabChange("members")}
            disabled={isActionInProgress}
          >
            Members ({members.length})
          </button>
          <button
            className={`tab-button ${activeTab === "events" ? "active" : ""}`}
            onClick={() => handleTabChange("events")}
            disabled={isActionInProgress}
          >
            Events
          </button>
          <button
            className={`tab-button ${activeTab === "tasks" ? "active" : ""}`}
            onClick={() => handleTabChange("tasks")}
            disabled={isActionInProgress}
          >
            Tasks
          </button>
        </div>

        <div className="action-message-container" style={{ margin: "15px 0" }}>
          {isActionInProgress ? (
            <div className="action-message action-message-pending">
              <div className="spinner-small"></div>
              <span style={{ marginLeft: "10px" }}>
                Applying action... please wait.
              </span>
            </div>
          ) : (
            actionMessage && (
              <div
                key={messageKey}
                className={`action-message action-message-${
                  actionMessage.type
                } ${isMessageFading ? "fading-out" : ""}`}
              >
                {actionMessage.message}
              </div>
            )
          )}
        </div>

        <div className="clan-tab-content">{renderActiveTab()}</div>
      </div>

      <Modal
        isOpen={isEditNameModalOpen}
        title="Edit Clan Name"
        message={
          <div className="edit-name-form-container">
            <p style={{ marginBottom: "10px" }}>
              Enter the new name for the clan.
            </p>
            <div style={{ textAlign: "left", marginTop: "10px" }}>
              <label htmlFor="new_clan_name" className="modal-form-label">
                New Clan Name:
              </label>
              <input
                id="new_clan_name"
                type="text"
                className="modal-form-field"
                value={newClanNameInput}
                onChange={(e) => setNewClanNameInput(e.target.value)}
                placeholder="e.g., The Elite Scapers"
                maxLength={40}
                style={{
                  width: "100%",
                  padding: "10px",
                  boxSizing: "border-box",
                }}
                disabled={isActionInProgress}
              />
            </div>
          </div>
        }
        onConfirm={executeEditName}
        onCancel={() => setIsEditNameModalOpen(false)}
        confirmButtonText="Save New Name"
        confirmButtonClassName="btn-primary"
        confirmDisabled={
          newClanNameInput.trim() === "" ||
          newClanNameInput.trim() === clanName ||
          isActionInProgress
        }
      />

      <Modal
        isOpen={isDisbandModalOpen}
        title="Confirm Clan Disband"
        message={`WARNING: Disbanding ${clanName} is permanent. All clan data will be lost. Are you absolutely sure?`}
        onConfirm={executeDisband}
        onCancel={() => setIsDisbandModalOpen(false)}
        confirmButtonText="Yes, Disband Clan"
        confirmDisabled={isActionInProgress}
        confirmButtonClassName="btn-danger"
      />
    </>
  );
}

export default ClanManagePage;
